// @ts-nocheck

import { useQueryClient } from '@tanstack/react-query';
import { get } from 'lodash';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import * as party from 'party-js';
import { useAlgo } from '../hooks/useAlgo';
import { useAllKitties } from '../hooks/useAllKitties';
import { useClaimJobMutation, useGenerateJobsMutation, useJobsQuery, useRateJobMutation, useStartJobMutation } from '../hooks/useJobs';
import { useKittiesStore } from '../hooks/useKittiesStore';
import { useMyCities } from '../hooks/useMyCities';
import useJobGenerationCooldown from '../hooks/useJobGenerationCooldown';
import useAnimationControls from '../hooks/useAnimationControls';
import { JobT } from '../utils/jobs/types';
import Button from './Button';
import CityImage from './cities/CityImage';
import CompletedJobsFeed from './CompletedJobsFeed';
import ConnectWalletMessage from './ConnectWalletMessage';
import Container from './Container';
import Countdown from './Countdown';
import EmptyState from './EmptyState';
import FullScreenLoader from './FullScreenLoader';
import JobKittyDisplay from './JobKittyDisplay';
import JobRating from './JobRating';
import PageHeader from './PageHeader';
import { ShufflingJobText } from './ShufflingJobText';
import Spinner from './Spinner';
import WeatherEffects from './jobs/WeatherEffects';
import WeatherBadge from './jobs/WeatherBadge';
import Tabs from './Tabs';

const JobCard = ({ job, activeJobsCount, shouldAnimate }) => {
  const { allKitties } = useAllKitties();
  const { myKitties } = useKittiesStore();
  const { myCities } = useMyCities();
  const queryClient = useQueryClient();
  const { address, optInForJobReward } = useAlgo();

  const { mutate: startJob, isLoading: startJobLoading } = useStartJobMutation({
    onSuccess: (startedJob) => {
      // Update the job in the query cache
      queryClient.setQueryData(['jobs', address], (old: JobT[]) => {
        return old?.map((job) => (job.id === startedJob.id ? startedJob : job)) || [];
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to start job. Please try again.');
    },
  });

  const { mutate: claimJob, isLoading: claimJobLoading } = useClaimJobMutation({
    onSuccess: (data) => {
      toast.success(`Claimed ${data.rewards.treats} Shitty Treats!`);

      // Trigger confetti effect
      party.confetti(document.body, {
        count: party.variation.range(80, 120),
        spread: party.variation.range(50, 70),
      });

      // Update the job in the query cache
      queryClient.setQueryData(['jobs', address], (old: JobT[]) => {
        return old?.map((j) => (j.id === job.id ? { ...j, status: 'completed', claimed: true } : j)) || [];
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to claim job rewards. Please try again.');
    },
  });

  const { mutate: rateJob } = useRateJobMutation({
    onSuccess: (data, variables) => {
      toast.success('Thank you for rating this job!');
      // Update the job in the query cache
      queryClient.setQueryData(['jobs', address], (old: JobT[]) => {
        return old?.map((j) => (j.id === job.id ? { ...j, rating: variables.rating, ratedAt: new Date() } : j)) || [];
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to submit rating. Please try again.');
    },
  });

  const handleStartJob = () => {
    startJob(job.id);
  };

  const handleClaimJob = async () => {
    try {
      // Create opt-in transaction for Shitty Treats only (no treasure boxes)
      const txns = await optInForJobReward();
      claimJob({ jobId: job.id, txn: txns[0].txId });
    } catch (error) {
      toast.error('Failed to create claim transaction');
    }
  };

  // Find kitties and city based on IDs from the new schema
  const requiredKitties = job.kitties?.map((kittyId) => myKitties.find((kitty) => kitty.id === kittyId)).filter(Boolean) || [];

  const requiredCity = job.city ? myCities.find((city) => city.index === job.city) : null;

  // Check if at job limit
  const isAtJobLimit = activeJobsCount >= 6;

  const startedAt = useMemo(() => (job?.startedAt ? new Date(job.startedAt) : null), [job.startedAt]);
  const endDate = useMemo(() => (startedAt ? new Date(startedAt.getTime() + job.duration * 24 * 60 * 60 * 1000) : null), [startedAt, job.duration]);

  // Check if enough time has passed since the job was started
  const hasEnoughTimePassed = useMemo(() => {
    if (!endDate) return false;

    const now = new Date();
    const isCompleted = now >= endDate;
    return isCompleted;
  }, [endDate]);

  const showRewardDetails = job.reward && !hasEnoughTimePassed;
  const environmentVariant = job.environment || null;
  const cardTone = useMemo(() => {
    if (environmentVariant === 'snow') return 'from-sky-500/20 via-sky-500/10 to-sky-400/10 border-sky-500/30';
    if (environmentVariant === 'rain') return 'from-blue-500/25 via-blue-500/10 to-indigo-500/10 border-blue-500/30';
    if (environmentVariant === 'sunny') return 'from-amber-500/25 via-yellow-500/10 to-orange-500/10 border-amber-400/30';
    if (environmentVariant === 'space') return 'from-purple-600/25 via-indigo-600/15 to-black/20 border-purple-500/30';
    if (environmentVariant === 'jungle') return 'from-green-600/25 via-emerald-600/15 to-green-500/10 border-green-500/30';
    if (environmentVariant === 'ocean') return 'from-cyan-600/25 via-blue-600/15 to-teal-500/10 border-cyan-500/30';
    if (environmentVariant === 'arctic') return 'from-slate-400/25 via-slate-300/15 to-slate-200/10 border-slate-400/30';
    return 'from-neutral-800 via-neutral-800 to-neutral-800 border-gray-700';
  }, [environmentVariant]);

  return (
    <div className={`relative overflow-hidden rounded-2xl border backdrop-blur-xl bg-gradient-to-br ${cardTone}`}>
      <div className='absolute inset-0 opacity-60 -z-10'>
        <WeatherEffects variant={environmentVariant} shouldAnimate={shouldAnimate} />
      </div>
      <div className='relative p-6 flex flex-col justify-between flex-1 h-full'>
        <div className='flex-1 h-full flex flex-col justify-between'>
          <div className='flex-1 h-full'>
            <div className='flex items-center justify-between gap-8 mb-2'>
              <h3 className='text-lg leading-tight font-semibold text-neutral-200'>{job.title}</h3>
              {/* <WeatherBadge variant={environmentVariant} /> */}
            </div>
            <p className='text-neutral-400 mb-4'>{job.description}</p>
          </div>

          <div>
            {/* Required Kitties Section */}
            {requiredKitties.length > 0 && (
              <div className='mb-4'>
                <h4 className='text-sm font-medium text-neutral-300 mb-3'>Required Kitties:</h4>
                <div className='flex gap-2 flex-wrap'>
                  {requiredKitties.map((kitty, index) => (
                    <JobKittyDisplay key={index} kitty={kitty} showEnergyDrink={job.status === 'completed'} />
                  ))}
                </div>
              </div>
            )}

            {/* Required City Section */}
            {requiredCity && (
              <div className='mb-4'>
                <h4 className='text-sm font-medium text-neutral-300 mb-3'>Required City:</h4>
                <div className='flex items-center space-x-3'>
                  <div className='w-20 h-14 rounded-lg overflow-hidden bg-gray-800'>
                    <CityImage imageUrl={requiredCity.image} blocks={[]} cityImageUrl={requiredCity.image} />
                  </div>
                  <div>
                    <span className='text-sm text-neutral-200 font-medium'>
                      {requiredCity.details?.name || requiredCity.params?.name || `City #${requiredCity.index}`}
                    </span>
                    <p className='text-xs text-neutral-400'>#{requiredCity.index}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Job Details Section */}
            <div className='space-y-3'>
              {/* Duration & Cooldown */}
              {(job.duration || job.cooldown) && (
                <div className='grid grid-cols-2 gap-2'>
                  {job.duration && (
                    <div className='bg-gray-500/20 border border-gray-500/30 rounded-lg p-2'>
                      <div className='text-center'>
                        <div className='text-xs text-neutral-400 mb-1'>Duration</div>
                        <div className='text-sm font-medium text-gray-400'>{job.duration}d</div>
                      </div>
                    </div>
                  )}

                  {job.cooldown && (
                    <div className='bg-gray-500/20 border border-gray-500/30 rounded-lg p-2'>
                      <div className='text-center'>
                        <div className='text-xs text-neutral-400 mb-1'>Cooldown</div>
                        <div className='text-sm font-medium text-gray-400'>{job.cooldown}d</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Reward */}
              {showRewardDetails && (
                <div className='bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border border-orange-500/30 rounded-lg p-3'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium text-neutral-300'>Reward:</span>
                    <div className='flex items-center space-x-1'>
                      <span className='text-lg font-bold text-orange-400'>{job.reward.toLocaleString()}</span>
                      <span className='text-sm text-orange-300'>Shitty Treats</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Countdown for started jobs */}
        {job.status === 'started' &&
          job.startedAt &&
          job.duration &&
          (() => {
            return (
              <div className='mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg'>
                <div className='text-center'>
                  {hasEnoughTimePassed ? (
                    <>
                      <div className='text-xs text-green-400 mb-2'>Job completed!</div>
                      <div className='text-sm text-green-300 mb-3'>Ready to collect {job.reward} Shitty Treats</div>
                      <Button onClick={handleClaimJob} theme='primary' size='sm' disabled={claimJobLoading || job.claimed} className='w-full'>
                        {claimJobLoading ? 'Claiming...' : job.claimed ? 'Claimed' : 'Claim Rewards'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className='text-xs text-blue-400 mb-2'>Job completes in:</div>
                      <div className='text-sm text-blue-300'>
                        <Countdown date={endDate} simple={true} />
                      </div>
                      <div className='text-xs text-blue-200 mt-1'>End: {endDate.toLocaleString()}</div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}

        {job.status === 'available' && (
          <div className='flex gap-3 mt-6'>
            <Button
              onClick={handleStartJob}
              theme='primary'
              className='w-full'
              disabled={startJobLoading || job.status !== 'available' || isAtJobLimit}
              title={isAtJobLimit ? 'You have reached the maximum of 6 active jobs' : undefined}
            >
              {startJobLoading ? 'Starting...' : job.status === 'started' ? 'Started' : isAtJobLimit ? 'Job Limit Reached' : 'Start Job'}
            </Button>
          </div>
        )}

        {job.status === 'completed' && job.claimed && (
          <JobRating
            job={job}
            onRatingSubmitted={() => {
              // Refresh the job data to show the rating
              queryClient.invalidateQueries(['jobs', address]);
            }}
          />
        )}
      </div>
    </div>
  );
};

const TheJobsPage = () => {
  const { address } = useAlgo();
  const queryClient = useQueryClient();
  const { data: jobsData, isLoading: jobsLoading } = useJobsQuery();
  const { animationsEnabled, shouldAnimate, toggleAnimations } = useAnimationControls();
  const [activeTab, setActiveTab] = useState('available');

  // Filter jobs by status
  const availableJobs = jobsData?.filter((job) => job.status === 'available') || [];
  const activeJobs = jobsData?.filter((job) => job.status === 'started') || [];
  const completedJobs = jobsData?.filter((job) => job.status === 'completed') || [];
  const activeJobsCount = activeJobs.length;

  // Ensure kitties and cities data is available
  const { allKitties, isLoading: kittiesLoading } = useAllKitties();
  const { myCities, isLoading: citiesLoading } = useMyCities();
  const { myKitties, myKittiesLoading } = useKittiesStore();

  // Check job generation cooldown
  const { isOnCooldown, cooldownEndsAt, loading: cooldownLoading, refetch: refetchCooldown } = useJobGenerationCooldown();

  const {
    mutate: generateJobs,
    isLoading: generateJobsLoading,
    error: generateJobsError,
  } = useGenerateJobsMutation({
    onSuccess: (data) => {
      // Replace available jobs with new ones, keep other jobs (started, completed, etc.)
      queryClient.setQueryData(['jobs', address], (old: JobT[]) => {
        const nonAvailableJobs = old?.filter((job) => job.status !== 'available') || [];
        return [...nonAvailableJobs, ...data];
      });
      // Refetch cooldown after successful generation
      refetchCooldown();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to generate jobs. Please try again.');
      // Refetch cooldown after error (might have cooldown info)
      refetchCooldown();
    },
  });

  // Get kitty names for the shuffling text
  const kittyNames = myKitties?.map((kitty) => kitty.name || `Kitty #${kitty.id}`).filter(Boolean) || [];

  // Determine button state and text
  const getButtonContent = () => {
    if (generateJobsLoading) {
      return 'Searching for work...';
    }

    if (isOnCooldown && cooldownEndsAt) {
      return (
        <div className='flex flex-col items-center'>
          <span className='text-xs opacity-75 mb-1'>More jobs available in:</span>
          <Countdown date={new Date(cooldownEndsAt)} simple={true} />
        </div>
      );
    }

    return 'Search for a job';
  };

  const isButtonDisabled = generateJobsLoading || isOnCooldown;

  const tabs = [
    { id: 'available', name: 'Available', count: availableJobs.length },
    { id: 'active', name: 'Active', count: activeJobs.length },
    { id: 'completed', name: 'Completed', count: completedJobs.length },
  ];

  return (
    <>
      <FullScreenLoader loading={generateJobsLoading} label='Searching for work...'>
        <Container>
          <ShufflingJobText names={kittyNames} className='!text-lg text-white/80' />
        </Container>
      </FullScreenLoader>

      <PageHeader
        heading='Shitty Jobs'
        description='An AI-powered Shitty Job Board. Earn Shitty Treats by completing jobs in your Shitty City with your Shitty Kitties.'
        docsLink='https://docs.shittykitties.art/website-utilities/shitty-jobs'
      />

      {!address ? (
        <Container>
          <div className='flex justify-center py-20'>
            <ConnectWalletMessage />
          </div>
        </Container>
      ) : jobsLoading || kittiesLoading || citiesLoading || myKittiesLoading ? (
        <Container>
          <div className='flex justify-center py-20'>
            <Spinner dimensionClasses='w-8 h-8' />
          </div>
        </Container>
      ) : myKitties.length === 0 || myCities.length === 0 ? (
        <Container>
          <div className='flex justify-center py-20'>
            <EmptyState message='You must own a Shitty Kitty and a Shitty City to participate in Shitty Jobs.' />
          </div>
        </Container>
      ) : (
        <Container>
          <div className='pt-20'>
            <div className='flex justify-between flex-col md:flex-row md:items-center mb-6 gap-4'>
              <div className='flex items-center gap-3 flex-col md:flex-row'>
                <div className='flex flex-col items-end w-full md:w-auto'>
                  <Button theme='outline' onClick={() => generateJobs()} disabled={isButtonDisabled} className='min-w-[260px] w-full md:w-auto'>
                    {getButtonContent()}
                  </Button>
                  {generateJobsError?.isRateLimit && <p className='text-sm text-red-400 mt-1'>{generateJobsError.message}</p>}
                </div>

                <button
                  onClick={toggleAnimations}
                  className={`px-3 py-2 h-[44px] text-sm rounded-lg border transition-colors ${animationsEnabled
                    ? 'bg-green-600/20 text-green-400 border-green-500/30 hover:bg-green-600/30'
                    : 'bg-red-600/20 text-red-400 border-red-500/30 hover:bg-red-600/30'
                    }`}
                  title={animationsEnabled ? 'Disable weather animations' : 'Enable weather animations'}
                >
                  {animationsEnabled ? '🎬 Animations On' : '🚫 Animations Off'}
                </button>
              </div>
            </div>

            <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

            {!availableJobs?.length && !activeJobs?.length && !completedJobs?.length ? (
              <div className='max-w-4xl mx-auto text-center py-20 flex justify-center'>
                <EmptyState
                  icon='city'
                  message='Put your Shitty Kitties to work.'
                  action={{
                    onClick: () => generateJobs(),
                    text: 'Look for work',
                    loading: generateJobsLoading,
                  }}
                >
                  {generateJobsLoading && (
                    <div className='-mt-4'>
                      <ShufflingJobText names={allKitties.map((kitty) => get(kitty, 'bio.bio.name', kitty.name))} />
                    </div>
                  )}
                </EmptyState>
              </div>
            ) : (
              <div className='mb-16'>
                {activeTab === 'available' && (
                  <>
                    {availableJobs.length > 0 ? (
                      <div className='grid gap-6 md:grid-cols-3 mt-6'>
                        {availableJobs.map((job, index) => (
                          <JobCard key={index} job={job} activeJobsCount={activeJobsCount} shouldAnimate={shouldAnimate} />
                        ))}
                      </div>
                    ) : (
                      <div className='max-w-4xl mx-auto text-center pt-20 flex justify-center'>
                        <EmptyState icon='city' message='Try searching for a job...' />
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'active' && (
                  <>
                    {activeJobs.length > 0 ? (
                      <>
                        <div className='flex justify-end items-center mb-6 mt-6'>
                          <div
                            className={`px-3 py-1 rounded-full text-sm font-medium ${activeJobsCount >= 6
                              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                              : activeJobsCount >= 4
                                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                : 'bg-green-500/20 text-green-400 border border-green-500/30'
                              }`}
                          >
                            Active Jobs: {activeJobsCount}/6
                          </div>
                        </div>
                        <div className='grid gap-6 md:grid-cols-3'>
                          {activeJobs.map((job, index) => (
                            <JobCard key={index} job={job} activeJobsCount={activeJobsCount} shouldAnimate={shouldAnimate} />
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className='max-w-4xl mx-auto text-center pt-20 flex justify-center'>
                        <EmptyState icon='city' message='No active jobs. Start a job from the Available tab!' />
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'completed' && (
                  <>
                    {completedJobs.length > 0 ? (
                      <div className='grid gap-6 md:grid-cols-3 mt-6'>
                        {completedJobs.map((job, index) => (
                          <JobCard key={index} job={job} activeJobsCount={activeJobsCount} shouldAnimate={shouldAnimate} />
                        ))}
                      </div>
                    ) : (
                      <div className='max-w-4xl mx-auto text-center pt-20 flex justify-center'>
                        <EmptyState icon='city' message='No completed jobs yet. Complete some jobs to see them here!' />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Recent Job Completions Feed */}
            <CompletedJobsFeed />
          </div>
        </Container>
      )}
    </>
  );
};

export default TheJobsPage;
